import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Copy, Landmark, WalletCards } from 'lucide-react';
import { toast } from 'sonner';
import { api, bearerAuthConfig, formatApiErrorDetail } from '../../core/api';
import { t } from '../../core/i18n';
import { AFFILIATE } from '../../constants/testIds';

const money = (minor) => `RM${((Number(minor) || 0) / 100).toFixed(2)}`;

function statusLabel(status) {
  if (status === 'PAID') return t('affiliateStatusPaid');
  if (status === 'REJECTED') return t('affiliateStatusRejected');
  return t('affiliateStatusPending');
}

export function createWithdrawalRequestId() {
  return globalThis.crypto?.randomUUID?.()
    || `affiliate-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function WithdrawalForm({
  maximumMinor,
  policy,
  onCancel,
  onCreated,
  requestIdFactory = createWithdrawalRequestId,
}) {
  const [amount, setAmount] = useState((maximumMinor / 100).toFixed(2));
  const [holder, setHolder] = useState('');
  const [bank, setBank] = useState('');
  const [account, setAccount] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const requestIdRef = useRef(null);

  const currentRequestId = () => {
    if (!requestIdRef.current) requestIdRef.current = requestIdFactory();
    return requestIdRef.current;
  };

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const response = await api.post('/affiliate/withdrawals', {
        amountMinor: Math.round(Number(amount) * 100),
        // Keep this key after any failure. A timeout can happen after the
        // server commits, so changing it on retry could reserve the wallet
        // twice. Only confirmed success or an explicit cancel starts anew.
        clientRequestId: currentRequestId(),
        accountHolderName: holder,
        bankName: bank,
        accountNumber: account,
        consent,
        consentVersion: 'affiliate-bank-v1',
      }, bearerAuthConfig());
      // Bank inputs are cleared immediately and are never persisted locally.
      setHolder('');
      setBank('');
      setAccount('');
      setConsent(false);
      requestIdRef.current = null;
      onCreated(response.data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-cream rounded-2xl p-5 mt-5 space-y-3" data-testid={AFFILIATE.withdrawalForm}>
      <label className="block font-body text-sm font-bold text-navy">
        {t('affiliateAvailable')}
        <input
          data-testid="affiliate-amount-input"
          type="number"
          min={(policy.minimumWithdrawalMinor / 100).toFixed(2)}
          max={(maximumMinor / 100).toFixed(2)}
          step="0.01"
          required
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          className="mt-1 w-full h-12 rounded-xl border-2 border-navy/10 bg-white px-4 font-body text-navy"
        />
      </label>
      <label className="block font-body text-sm font-bold text-navy">
        {t('affiliateHolder')}
        <input data-testid={AFFILIATE.holderNameInput} value={holder} onChange={(event) => setHolder(event.target.value)}
          autoComplete="name" required maxLength={120}
          className="mt-1 w-full h-12 rounded-xl border-2 border-navy/10 bg-white px-4 font-body text-navy" />
      </label>
      <label className="block font-body text-sm font-bold text-navy">
        {t('affiliateBank')}
        <input data-testid={AFFILIATE.bankNameInput} value={bank} onChange={(event) => setBank(event.target.value)}
          autoComplete="off" required maxLength={120}
          className="mt-1 w-full h-12 rounded-xl border-2 border-navy/10 bg-white px-4 font-body text-navy" />
      </label>
      <label className="block font-body text-sm font-bold text-navy">
        {t('affiliateAccount')}
        <input data-testid={AFFILIATE.accountNumberInput} value={account} onChange={(event) => setAccount(event.target.value)}
          inputMode="numeric" autoComplete="off" required maxLength={40}
          className="mt-1 w-full h-12 rounded-xl border-2 border-navy/10 bg-white px-4 font-body text-navy" />
      </label>
      <label className="flex items-start gap-3 font-body text-xs text-navy/70 leading-relaxed">
        <input data-testid={AFFILIATE.consentCheckbox} type="checkbox" checked={consent}
          onChange={(event) => setConsent(event.target.checked)} required className="mt-1 w-5 h-5 shrink-0" />
        <span>{t('affiliateConsent')}</span>
      </label>
      <p className="font-body text-xs text-navy/60">{t('affiliatePayoutPromise')}</p>
      <div className="flex flex-wrap gap-3">
        <button data-testid={AFFILIATE.withdrawalSubmitButton} type="submit"
          disabled={submitting || !consent}
          className="min-h-[44px] rounded-full bg-navy text-white px-5 font-body font-bold disabled:opacity-50">
          {submitting ? t('affiliateSubmitting') : t('affiliateSubmit')}
        </button>
        <button data-testid={AFFILIATE.withdrawalCancelButton} type="button"
          onClick={() => { requestIdRef.current = null; onCancel(); }}
          className="min-h-[44px] rounded-full bg-white text-navy px-5 font-body font-bold">
          {t('cancel')}
        </button>
      </div>
    </form>
  );
}

export default function ParentAffiliatePanel({ user, isPro, onEntitlementLost }) {
  const [dashboard, setDashboard] = useState(null);
  const [state, setState] = useState('loading');
  const [showForm, setShowForm] = useState(false);
  const [created, setCreated] = useState(false);

  const load = useCallback(async () => {
    if (!user || !isPro) {
      setState('locked');
      return;
    }
    setState('loading');
    try {
      const response = await api.get('/affiliate/me', bearerAuthConfig());
      setDashboard(response.data);
      setState('ready');
    } catch (error) {
      if (error.response?.status === 403) {
        setState('locked');
        onEntitlementLost?.();
      } else {
        setState('error');
      }
    }
  }, [user, isPro, onEntitlementLost]);

  useEffect(() => { load(); }, [load]);

  const referralLink = useMemo(() => (
    dashboard?.code
      ? `${window.location.origin}/?ref=${encodeURIComponent(dashboard.code)}`
      : ''
  ), [dashboard]);

  if (state === 'loading') {
    return <div className="mt-6 font-body text-navy/60" data-testid={AFFILIATE.loading}>{t('affiliateSubmitting')}</div>;
  }
  if (state === 'locked') {
    return <div className="mt-6 bg-white rounded-3xl p-6 font-body text-navy" data-testid={AFFILIATE.locked}>{t('affiliateLocked')}</div>;
  }
  if (state === 'error' || !dashboard) {
    return (
      <div className="mt-6 bg-white rounded-3xl p-6" data-testid={AFFILIATE.panel}>
        <p className="font-body text-coral">{t('affiliateLoadError')}</p>
        <button onClick={load} className="mt-3 min-h-[44px] underline font-body text-navy">{t('replay')}</button>
      </div>
    );
  }

  const { wallet, policy, withdrawals = [] } = dashboard;
  const canWithdraw = wallet.availableMinor >= policy.minimumWithdrawalMinor;

  return (
    <section className="mt-6 space-y-5" data-testid={AFFILIATE.panel}>
      <div className="bg-white/90 rounded-3xl p-6 shadow-sm">
        <div className="flex items-center gap-2"><WalletCards className="text-leaf" /><h2 className="font-heading text-xl font-bold text-navy">{t('affiliateTitle')}</h2></div>
        <p className="font-body text-sm text-navy/65 mt-2">{t('affiliateIntro')}</p>
        <label className="block font-body text-xs font-bold text-navy/55 mt-5">{t('affiliateShareLink')}</label>
        <div className="flex gap-2 mt-1 flex-wrap">
          <input readOnly value={referralLink} data-testid={AFFILIATE.referralLink}
            className="flex-1 min-w-0 h-12 rounded-xl bg-cream px-4 font-body text-sm text-navy" />
          <button data-testid={AFFILIATE.copyLinkButton}
            onClick={async () => {
              await navigator.clipboard.writeText(referralLink);
              toast.success(t('affiliateCopied'));
            }}
            className="min-h-[44px] rounded-xl bg-sunshine px-5 font-body font-bold text-navy flex items-center gap-2">
            <Copy size={16} />{t('affiliateCopyLink')}
          </button>
        </div>
      </div>

      <div className="bg-white/90 rounded-3xl p-6 shadow-sm">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-cream rounded-2xl p-3" data-testid={AFFILIATE.totalEarned}><p className="font-body text-xs text-navy/50">{t('affiliateLifetimeEarned')}</p><b className="font-heading text-xl text-navy">{money(wallet.lifetimeEarnedMinor)}</b></div>
          <div className="bg-cream rounded-2xl p-3" data-testid={AFFILIATE.pendingBalance}><p className="font-body text-xs text-navy/50">{t('affiliatePending')}</p><b className="font-heading text-xl text-navy">{money(wallet.pendingMinor)}</b></div>
          <div className="bg-cream rounded-2xl p-3" data-testid={AFFILIATE.availableBalance}><p className="font-body text-xs text-navy/50">{t('affiliateAvailable')}</p><b className="font-heading text-xl text-navy">{money(wallet.availableMinor)}</b></div>
          <div className="bg-cream rounded-2xl p-3" data-testid={AFFILIATE.paidBalance}><p className="font-body text-xs text-navy/50">{t('affiliatePaid')}</p><b className="font-heading text-xl text-navy">{money(wallet.withdrawnPaidMinor)}</b></div>
        </div>
        {wallet.withdrawalReservedMinor > 0 && <p className="font-body text-xs text-navy/60 mt-3">{t('affiliateReserved')}: {money(wallet.withdrawalReservedMinor)}</p>}
        {wallet.availableMinor < 0 && <p className="font-body text-sm text-coral font-bold mt-3" data-testid={AFFILIATE.negativeBalance}>{t('affiliateNegative')}: {money(wallet.availableMinor)}</p>}
        <p className="font-body text-xs text-navy/55 mt-4" data-testid={AFFILIATE.minimumNote}>{t('affiliateMinimum')}</p>
        <p className="font-body text-xs text-navy/55 mt-1">{t('affiliateHoldNote')}</p>
        <p className="font-body text-xs text-coral/90 mt-1">{t('affiliateRecurringNote')}</p>
        {!showForm && (
          <button data-testid={AFFILIATE.withdrawButton} disabled={!canWithdraw} onClick={() => { setShowForm(true); setCreated(false); }}
            className="mt-4 min-h-[44px] rounded-full bg-navy text-white px-6 font-body font-bold disabled:opacity-40 flex items-center gap-2">
            <Landmark size={17} />{t('affiliateWithdraw')}
          </button>
        )}
        {showForm && <WithdrawalForm maximumMinor={wallet.availableMinor} policy={policy}
          onCancel={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); setCreated(true); load(); }} />}
        {created && <p className="font-body text-green-800 font-bold mt-4" data-testid={AFFILIATE.withdrawalSuccess}>{t('affiliateRequested')}</p>}
        <a data-testid={AFFILIATE.supportLink} href={`mailto:${policy.supportEmail}`}
          className="inline-block mt-4 font-body text-sm text-sky underline">{t('affiliateSupport')}: {policy.supportEmail}</a>
      </div>

      <div className="bg-white/90 rounded-3xl p-6 shadow-sm" data-testid={AFFILIATE.history}>
        <h3 className="font-heading text-lg font-bold text-navy">{t('affiliateHistory')}</h3>
        {withdrawals.length === 0 ? (
          <p className="font-body text-sm text-navy/50 mt-3" data-testid={AFFILIATE.historyEmpty}>{t('affiliateHistoryEmpty')}</p>
        ) : withdrawals.map((item) => (
          <div key={item.id} data-testid={`affiliate-withdrawal-${item.id}`} className="flex items-center justify-between gap-3 py-3 border-b border-navy/5 last:border-0">
            <div><p className="font-body font-bold text-navy">{money(item.amountMinor)}</p><p className="font-body text-xs text-navy/45">{new Date(item.requestedAt).toLocaleDateString()}</p></div>
            <span className="font-body text-xs font-bold rounded-full bg-cream px-3 py-1">{statusLabel(item.status)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
